import React from 'react';
import noop from 'lodash/noop';
import { graphql, useLazyLoadQuery } from 'react-relay';
import DropdownMenu, { DropdownItem, type OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useProjectsAnalyticsL3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l3/index.tsx';
import { MOVE_IN_SIDEBAR } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import type { MoveInSidebarMenuItemQuery } from '@atlassian/jira-relay/src/__generated__/MoveInSidebarMenuItemQuery.graphql';
import { getPositionType } from '../utils/position-in-list.tsx';
import { useUpdateFavouriteOrder } from '../services/update-favourite-order/index.tsx';
import { moveUp, moveDown, moveToTop, moveToBottom } from '../utils/reorder-list.tsx';
import { MoveInSidebarMenu } from './move-menu/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const MoveInSidebarMenuItem = ({
	onOpenChange = noop,
	projectId,
	projectType,
	onMoveActionComplete,
}: Props) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { sendMeatBallDropDownOnProjectsMenuItemAnalyticsEvent } = useProjectsAnalyticsL3();

	const data = useLazyLoadQuery<MoveInSidebarMenuItemQuery>(
		graphql`
			query MoveInSidebarMenuItemQuery($cloudId: ID!) {
				jira_projectsSidebarMenu(cloudId: $cloudId)
					@optIn(to: "JiraSidebarMenu")
					@required(action: THROW) {
					favourites(first: 15) @required(action: THROW) {
						__id
						edges {
							node {
								projectId
							}
						}
					}
				}
			}
		`,
		{ cloudId },
	);

	const favourites = data.jira_projectsSidebarMenu.favourites;
	const favouritesEdges = favourites?.edges ?? [];

	const isDisabled = favouritesEdges.length <= 1;
	const index = favouritesEdges.findIndex((edge) => edge?.node?.projectId === projectId) ?? -1;
	const listSize = favouritesEdges.length ?? 0;
	const positionType = getPositionType({ index, listSize });
	const projectIds = favouritesEdges.map((edge) => edge?.node?.projectId).filter(Boolean);

	const updateFavouriteOrder = useUpdateFavouriteOrder(favourites.__id);

	const onOpenChangeWithAnalytics = (args: OnOpenChangeArgs) => {
		const { isOpen } = args;
		if (isOpen) {
			sendMeatBallDropDownOnProjectsMenuItemAnalyticsEvent({
				itemId: MOVE_IN_SIDEBAR,
				projectId,
				projectType,
				starred: true,
				recent: false,
			});
		}
		onOpenChange(args);
	};

	return (
		<DropdownMenu
			onOpenChange={onOpenChangeWithAnalytics}
			shouldRenderToParent
			placement="right-start"
			trigger={({ triggerRef, ...triggerProps }) => (
				<DropdownItem
					{...triggerProps}
					ref={triggerRef}
					elemBefore={<GrowVerticalIcon label="" LEGACY_size="medium" spacing="spacious" />}
					elemAfter={<ChevronRightIcon label="" LEGACY_size="medium" spacing="spacious" />}
					isDisabled={isDisabled}
				>
					{formatMessage(messages.moveInSidebarMenu)}
				</DropdownItem>
			)}
		>
			<MoveInSidebarMenu
				projectId={projectId}
				projectType={projectType}
				onMoveUp={() => {
					updateFavouriteOrder(moveUp(projectIds, projectId));
					onMoveActionComplete?.(null);
				}}
				onMoveDown={() => {
					updateFavouriteOrder(moveDown(projectIds, projectId));
					onMoveActionComplete?.(null);
				}}
				onMoveToTop={() => {
					updateFavouriteOrder(moveToTop(projectIds, projectId));
					onMoveActionComplete?.(null);
				}}
				onMoveToBottom={() => {
					updateFavouriteOrder(moveToBottom(projectIds, projectId));
					onMoveActionComplete?.(null);
				}}
				positionType={positionType}
			/>
		</DropdownMenu>
	);
};
